// lib/director.ts — the Dashboard's director summary, portfolio roll-ups and home blocks (Task M). Mirrors
// src/modules/reports/director.service.ts, rollups.service.ts, dashboardBlocks.ts and dashboardLayouts.service.ts.
import { api, tokenStore } from './api';
import type { ReportResult } from '../types/api';

export interface Drill { reportKey?: string; path?: string; filters?: Record<string, unknown> }
export interface Figure { value: number; currency?: string; count?: number; drill: Drill; note?: string; basis?: string }
export type Period = 'month' | 'quarter' | 'year';

export interface NeedsMeItem { id: string; kind: 'bill' | 'aiAction' | 'ownerStatement' | 'contract'; title: string; detail: string; amountMinor: number | null; currency: string | null; date: string | null; url: string }
export interface NeedsMe { billsAwaitingSignoff: NeedsMeItem[]; aiActionsPending: NeedsMeItem[]; ownerStatementsDraft: NeedsMeItem[]; contractsNoticeDue: NeedsMeItem[]; total: number }

export interface DirectorSummary {
  period: { from: string; to: string; period: Period }; currency: string; asOf: string;
  needsMe: NeedsMe;
  comingIn: { rentRollMinor: Figure | null; collectedPctMtd: Figure | null; feeIncomeMinor: Figure | null; otherIncome: Figure | null; trend: Array<{ month: string; raisedMinor: number; collectedMinor: number }> | null };
  goingOut: { maintenanceSpend: Figure | null; contractsAnnual: Figure | null; agentFees: Figure | null; voidsCostMinor: Figure | null; trend: Array<{ month: string; spendMinor: number }> | null };
  portfolio: Record<'units' | 'occupancyPct' | 'vacant' | 'onMarket' | 'changeoversInProgress' | 'moveInsNext30' | 'moveOutsNext30', Figure | null>;
  couldBite: Record<'complianceOverdue' | 'complianceDueSoon' | 'arrearsOver31Days' | 'depositsUnprotectedPastDue' | 'advancesOutstanding' | 'overdrawnProperties' | 'section20Pending' | 'insuranceExpiring', Figure | null>;
  /** Bank lines to reconcile — all null without banking.read. */
  bank: { unallocated: Figure | null; suggested: Figure | null; unmatchedDebits: Figure | null };
}

export type RollupKind = 'building' | 'property' | 'lettings' | 'accounts';
export type Rollup = ReportResult & { hiddenColumns?: string[] };

export interface BlockItem { id: string; title: string; sub?: string | null; date?: string | null; days?: number | null; url?: string | null; outstanding?: string[]; kind?: string }
export interface BlockData {
  key: string; title: string; kind: 'stat' | 'list' | 'moves' | 'note';
  value?: number | null; format?: 'number' | 'money' | 'percent'; currency?: string; hint?: string | null; tone?: 'good' | 'warning' | 'critical' | null;
  drill?: Drill | null; items?: BlockItem[]; text?: string | null; link?: string | null;
}
export interface LayoutBlock { key: string; hidden?: boolean; order: number; custom?: { title: string; text?: string | null; link?: string | null } | null; title: string; description: string; available: boolean }
export interface Layout { source: 'USER' | 'ROLE' | 'DEFAULT'; roleId: string; roleName: string; blocks: LayoutBlock[]; canArrangeRoles?: boolean }
export type LayoutSave = Array<{ key: string; hidden?: boolean; order: number; custom?: { title: string; text?: string | null; link?: string | null } | null }>;

export const directorApi = {
  summary: async (period: Period) => (await api.get<DirectorSummary>('/director/summary', { period })).data,
  rollup: async (kind: RollupKind) => (await api.get<Rollup>(`/rollups/${kind}`)).data,
  runReport: async (key: string, input: Record<string, unknown> = {}) => (await api.post<ReportResult>(`/reports/${key}/run`, input)).data,
  signOff: (billId: string, note?: string) => api.post(`/bills/${billId}/sign-off`, { note }),
  approveAi: (id: string) => api.post(`/ai/actions/${id}/approve`, {}),
  rejectAi: (id: string, reason: string) => api.post(`/ai/actions/${id}/reject`, { reason }),
  issueStatement: (id: string) => api.post(`/owner-statements/${id}/issue`, {}),
  blocks: async () => (await api.get<{ source: Layout['source']; blocks: BlockData[] }>('/dashboard/blocks')).data,
  layout: async () => (await api.get<Layout>('/dashboard/layout')).data,
  saveLayout: (blocks: LayoutSave) => api.put<Layout>('/dashboard/layout', { blocks }),
  resetLayout: () => api.delete<Layout>('/dashboard/layout'),
  roleLayout: async (roleId: string) => (await api.get<Layout>(`/dashboard/layout/roles/${roleId}`)).data,
  saveRoleLayout: (roleId: string, blocks: LayoutSave) => api.put<Layout>(`/dashboard/layout/roles/${roleId}`, { blocks }),
  resetRoleLayout: (roleId: string) => api.delete<Layout>(`/dashboard/layout/roles/${roleId}`),
};

export const fmtMoney = (minor: number | null | undefined, currency = 'GBP', compact = false) =>
  minor === null || minor === undefined ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency, maximumFractionDigits: 0, ...(compact && Math.abs(minor) >= 1_000_000_00 ? { notation: 'compact' as const, maximumFractionDigits: 1 } : {}) }).format(minor / 100);

/** Download a report as CSV through the authenticated export endpoint. */
export async function downloadReportCsv(key: string, filters: Record<string, string> = {}) {
  const url = `/api/v1/reports/${key}/export?${new URLSearchParams(filters)}`;
  const t = tokenStore.get()?.accessToken;
  const r = await fetch(url, { headers: { authorization: `Bearer ${t}` } });
  if (!r.ok) throw new Error(`Export failed (${r.status})`);
  const u = URL.createObjectURL(await r.blob());
  const a = document.createElement('a'); a.href = u; a.download = `${key}.csv`; a.click();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
}

export const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });

export const anyFigure = (...figs: Array<Figure | null | undefined>) => figs.some((f) => !!f);

/** The period toggle belongs to money in / money out — it is shown only when one of them has a figure. */
export function hasPeriodFigures(s: DirectorSummary | undefined): boolean {
  if (!s) return false;
  const i = s.comingIn; const o = s.goingOut;
  return anyFigure(i.rentRollMinor, i.collectedPctMtd, i.feeIncomeMinor, i.otherIncome, o.maintenanceSpend, o.contractsAnnual, o.agentFees, o.voidsCostMinor);
}
